import React, { useRef, useState, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useConfig } from '../ConfigProvider'
import cloneDeep from 'lodash/cloneDeep'
import FileItem from './FileItem'
import Button from '../Buttons'
import CloseButton from '../CloseButton'
import Notification from '../Notification'
import toast from '../toast'
import { Loading } from 'components/shared'
import { isArray } from 'lodash'
import moment from 'moment-timezone'

const filesToArray = (files) => Object.keys(files).map((key) => files[key])

const getImageNameFromUrl = (url) => {
    if (url) {
        const parts = url?.split('/')
        return parts[parts?.length - 1]
    }
    return ''
}

const getFileListFromUrls = (urls) => {
    const data =
        urls?.length > 0
            ? urls.map((url) =>
                  url?.url && url?.name
                      ? url
                      : {
                            name: getImageNameFromUrl(url),
                            url,
                        }
              )
            : []
    return data
}

const Upload = React.forwardRef((props, ref) => {
    const {
        dueDate = null,
        accept,
        beforeUpload,
        disabled,
        draggable,
        fileList,
        multiple,
        onChange,
        onFileRemove,
        showList,
        tip,
        uploadLimit,
        children,
        className,
        field,
        form,
        uploadedUrls,
        hideRemoveButton,
        loading,
        fileSize = 2,
        fileLimit = 5,
        ...rest
    } = props

    const fileInputField = useRef(null)
    const [files, setFiles] = useState(fileList)
    const [dragOver, setDragOver] = useState(false)

    const { themeColor, primaryColorLevel } = useConfig()

    useEffect(() => {
        setFiles(fileList)
    }, [fileList])

    useEffect(() => {
        if (uploadedUrls && uploadedUrls.length > 0) {
            const dataarr =
                typeof uploadedUrls === 'string'
                    ? JSON.parse(uploadedUrls)
                    : isArray(uploadedUrls)
                    ? uploadedUrls
                    : []
            setFiles(getFileListFromUrls(dataarr))
        }
    }, [uploadedUrls])

    const triggerMessage = (msg) => {
        toast.push(
            <Notification type="danger" duration={2000}>
                {msg || 'Upload Failed!'}
            </Notification>,
            {
                placement: 'top-center',
            }
        )
    }

    const pushFile = (newFiles, file) => {
        for (let f of newFiles) {
            file.push(f)
        }
        return file
    }

    const addNewFiles = (newFiles) => {
        let file = cloneDeep(files)
        if (typeof uploadLimit === 'number' && uploadLimit !== 0) {
            if (Object.keys(file).length >= uploadLimit) {
                if (uploadLimit === 1) {
                    file.shift()
                    file = pushFile(newFiles, file)
                }
                return filesToArray({ ...file })
            }
        }
        file = pushFile(newFiles, file)
        return filesToArray({ ...file })
    }

    const onNewFileUpload = (e) => {
        const { files: newFiles } = e.target
        const newFilesArray = Array.from(newFiles)

        if (newFilesArray.length > fileLimit) {
            triggerMessage(
                `You can only upload up to ${fileLimit} files at a time.`
            )
            return
        }

        const oversizedFiles = newFilesArray.filter(
            (file) => file.size > fileSize * 1024 * 1024
        )
        if (oversizedFiles.length > 0) {
            triggerMessage(
                `Each file must be less than or equal to ${fileSize}MB.`
            )
            return
        }

        const allowedExtensions = [
            '.pdf',
            '.jpeg',
            '.jpg',
            '.png',
            '.xlsx',
            '.xls',
            '.csv',
            '.doc',
            '.docx',
        ]
        const hasInvalidType = newFilesArray.some((file) => {
            const ext = file.name
                .substring(file.name.lastIndexOf('.'))
                .toLowerCase()
            return !allowedExtensions.includes(ext)
        })

        if (hasInvalidType) {
            triggerMessage(
                'Only PDF, JPEG, JPG, PNG, XLSX, XLS, CSV, DOC, and DOCX files are allowed.'
            )
            return
        }

        let result = true
        if (beforeUpload) {
            result = beforeUpload(newFiles, files)
            if (result === false) {
                triggerMessage()
                return
            }
            if (typeof result === 'string' && result.length > 0) {
                triggerMessage(result)
                return
            }
        }

        if (result) {
            let updatedFiles = addNewFiles(newFiles)
            setFiles(updatedFiles)
            onChange?.(updatedFiles, files)
        }
    }

    const removeFile = (fileIndex) => {
        const deletedFileList = files.filter((_, index) => index !== fileIndex)
        setFiles(deletedFileList)
        onFileRemove?.(deletedFileList)
    }

    const triggerUpload = (e) => {
        if (!disabled) {
            fileInputField.current?.click()
        }
        e.stopPropagation()
    }

    const renderChildren = () => {
        if (!draggable && !children) {
            return (
                <Button disabled={disabled} onClick={(e) => e.preventDefault()}>
                    Upload
                </Button>
            )
        }

        if (draggable && !children) {
            return <span>Choose a file or drag and drop here</span>
        }

        return children
    }

    const handleDragLeave = useCallback(() => {
        if (draggable) {
            setDragOver(false)
        }
    }, [draggable])

    const handleDragOver = useCallback(() => {
        if (draggable && !disabled) {
            setDragOver(true)
        }
    }, [draggable, disabled])

    const handleDrop = useCallback(() => {
        if (draggable) {
            setDragOver(false)
        }
    }, [draggable])

    const draggableProp = {
        onDragLeave: handleDragLeave,
        onDragOver: handleDragOver,
        onDrop: handleDrop,
    }

    const draggableEventFeedbackClass = `border-${themeColor}-${primaryColorLevel}`

    const uploadClass = classNames(
        'upload',
        draggable && `upload-draggable`,
        draggable && !disabled && `hover:${draggableEventFeedbackClass}`,
        draggable && disabled && 'disabled',
        dragOver && draggableEventFeedbackClass,
        dueDate && 'relative flex flex-col',
        className
    )

    const uploadInputClass = classNames(
        'upload-input',
        draggable && `draggable`
    )

    return (
        <Loading type="cover" loading={loading}>
            <div
                ref={ref}
                className={uploadClass}
                {...(draggable ? draggableProp : { onClick: triggerUpload })}
                {...rest}
            >
                {dueDate && (
                    <span
                        className={classNames(
                            'absolute top-[-20px] right-[-7px] flex justify-center items-center text-xs rounded-lg px-3 py-[4px] mt-2',
                            (moment(dueDate).isValid() &&
                                moment().isSameOrBefore(dueDate)) ||
                                !moment(dueDate).isValid()
                                ? 'bg-teal-600 text-white border-teal-500/30 shadow-md'
                                : 'bg-gray-400 text-white border-gray-500/30 shadow-md'
                        )}
                    >
                        {moment(dueDate).isValid()
                            ? `${
                                  moment().isSameOrBefore(dueDate)
                                      ? 'Due:'
                                      : 'Overdue:'
                              } ${moment(dueDate)?.format('MMM DD, YYYY')}`
                            : dueDate}
                    </span>
                )}
                <input
                    className={uploadInputClass}
                    type="file"
                    ref={fileInputField}
                    onChange={onNewFileUpload}
                    disabled={disabled}
                    multiple={multiple}
                    accept={accept}
                    title=""
                    value=""
                    {...field}
                    {...rest}
                />
                {renderChildren()}
            </div>
            {tip}
            {showList && (
                <div className="upload-file-list">
                    {files.map((file, index) => (
                        <FileItem file={file} key={file.name + index}>
                            {!hideRemoveButton && (
                                <CloseButton
                                    onClick={() => removeFile(index)}
                                    className="upload-file-remove"
                                />
                            )}
                        </FileItem>
                    ))}
                </div>
            )}
        </Loading>
    )
})

Upload.propTypes = {
    uploadLimit: PropTypes.number,
    draggable: PropTypes.bool,
    disabled: PropTypes.bool,
    showList: PropTypes.bool,
    multiple: PropTypes.bool,
    accept: PropTypes.string,
    tip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
}

Upload.defaultProps = {
    draggable: false,
    showList: true,
    disabled: false,
    fileList: [],
    uploadLimit: 100,
}

export default Upload
